// @owners { team: patients-team }
import { AltoSpinningLoader, Body, Card, Column, LgPadding } from '@alto/design-system';
import { CashPayInsurance, EmptyInsurance } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React from 'react';
import { selectUser } from '~shared/actions/ui/users';
import { updateCashPay } from '~shared/actions/users';
import { getUserCashPayOnly } from '~shared/features/users/selectors/getUserCashPayOnly';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurancesByUserID } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { InsuranceList } from '~web/features/insurances/components/InsuranceList';

export type Props = {
  readonly userID: number;
};

export const ProfileInsurances = ({ userID }: Props) => {
  const { trackEvent } = useAnalytics();
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { hasActiveInsurances, hasInsurances, insurances, isPending: loading } = useGetInsurancesByUserID({ userID });
  const isCashPayOnly = useSelectorShared((state) =>
    getUserCashPayOnly(state, {
      userID,
    }),
  );
  const isNeedsInsurance = !isCashPayOnly && !hasInsurances;

  const handleAddInsurance = () => {
    dispatch(selectUser(userID));
    navigate('RouteInsuranceAdd', { source: 'profile', userID });
  };

  const handleUpdateCashPay = async () => {
    const { success } = await dispatch(updateCashPay(userID, true));
    if (success) {
      trackEvent({
        event: EVENTS.CASH_PAY_ONLY_SET,
        params: {
          source: 'profile',
          'new value': true,
        },
      });
    }
  };

  if (loading) {
    return (
      <Card title="Benefits">
        <LgPadding>
          <Column center>
            <AltoSpinningLoader />
            <Body>Loading your benefits...</Body>
          </Column>
        </LgPadding>
      </Card>
    );
  }

  if (isCashPayOnly) {
    return (
      <Card title="Benefits">
        <LgPadding>
          <CashPayInsurance
            hasActiveInsurances={hasActiveInsurances}
            source="profile"
            insurances={insurances}
          />
        </LgPadding>
      </Card>
    );
  }

  if (isNeedsInsurance) {
    return (
      <Card title="Benefits">
        <LgPadding>
          <EmptyInsurance
            onAddInsurance={handleAddInsurance}
            onSelectCashPay={handleUpdateCashPay}
            inlineButton
          />
        </LgPadding>
      </Card>
    );
  }

  return (
    <Card title="Benefits">
      <LgPadding>
        <InsuranceList
          hasActiveInsurances={hasActiveInsurances}
          insurances={insurances}
          onAddInsurance={handleAddInsurance}
          source="profile"
        />
      </LgPadding>
    </Card>
  );
};
